import React, { useState } from "react";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";

export default function CollectionExplorerSidebar() {
  const {
    registeredCollections,
    registryLoading,
    registrySyncing,
    registrySource,
    registryError,
    documentLoadError,
    registryDiagnostics,
    discoverySource,
    favorites,
    recent,
    collectionName,
    collectionMeta,
    selectCollection,
    toggleFavorite,
    rediscoverCollections,
    registerCollectionByName,
    runtimeReady,
    runtimeMismatch,
  } = useFirestoreAdmin();
  const [search, setSearch] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerError, setRegisterError] = useState("");
  const [registering, setRegistering] = useState(false);

  const filtered = registeredCollections.filter((c) =>
    c.collectionName.toLowerCase().includes(search.toLowerCase())
  );

  // Dev verification: number rendered by React must match loaded list (minus search filter).
  if (typeof console !== "undefined") {
    console.info("[CollectionExplorerSidebar] render", {
      "collections rendered": registeredCollections.length,
      "engine used": registryDiagnostics?.engineUsed ?? null,
      "cloud function response": registryDiagnostics?.cloudFunctionResponse ?? null,
      "collections returned": registryDiagnostics?.collectionsReturned ?? null,
      "registry written": registryDiagnostics?.registryWritten ?? null,
      "registry read": registryDiagnostics?.registryRead ?? registeredCollections.length,
      runtimeProjectId: registryDiagnostics?.runtimeProjectId ?? null,
      filtered: filtered.length,
    });
  }

  const onRediscover = async () => {
    setRegisterError("");
    try {
      await rediscoverCollections();
    } catch (err) {
      setRegisterError(err?.message || "Rediscover failed");
    }
  };

  const onRegister = async () => {
    const name = registerName.trim();
    if (!name) return;
    setRegistering(true);
    setRegisterError("");
    try {
      await registerCollectionByName(name);
      selectCollection(name);
      setRegisterName("");
    } catch (err) {
      setRegisterError(err?.message || "Registration failed");
    } finally {
      setRegistering(false);
    }
  };

  const validFavorites = favorites.filter((c) => collectionMeta.has(c));
  const validRecent = recent.filter((c) => collectionMeta.has(c));

  return (
    <aside className="fa-card p-3">
      {!runtimeReady && !runtimeMismatch && (
        <p className="mb-3 text-sm text-slate-500">No active workspace connection.</p>
      )}
      {runtimeMismatch && (
        <p className="mb-3 text-sm text-red-600">Runtime mismatch detected. Reload runtime from the workspace bar.</p>
      )}
      <div className="mb-3 flex gap-2">
        <button
          type="button"
          className="fa-btn fa-btn-primary flex-1 text-xs"
          onClick={onRediscover}
          disabled={!runtimeReady || registrySyncing}
        >
          {registrySyncing ? "Rediscovering…" : "Rediscover"}
        </button>
      </div>
      {(discoverySource || registrySource) && (
        <p className="mb-2 text-[10px] text-slate-500">Source: {discoverySource || registrySource}</p>
      )}
      {registryDiagnostics && (
        <p className="mb-2 text-[10px] text-slate-500">
          Admin SDK {registryDiagnostics.adminSdkCollections ?? "—"} · Registry{" "}
          {registryDiagnostics.registryRead ?? registeredCollections.length} · Rendered{" "}
          {registeredCollections.length}
          {registryDiagnostics.cloudFunctionVersion
            ? ` · CF v${registryDiagnostics.cloudFunctionVersion}`
            : ""}
          {registryDiagnostics.engineUsed ? ` · ${registryDiagnostics.engineUsed}` : ""}
        </p>
      )}
      {(registerError || registryError || documentLoadError) && (
        <p className="mb-2 text-xs text-red-600">{registerError || registryError || documentLoadError}</p>
      )}
      <input
        className="mb-3 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
        placeholder="Search collections…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="mb-3 flex gap-2">
        <input
          className="flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
          placeholder="Register existing collection"
          value={registerName}
          onChange={(e) => setRegisterName(e.target.value)}
        />
        <button type="button" className="fa-btn" onClick={onRegister} disabled={registering}>
          {registering ? "…" : "+"}
        </button>
      </div>
      {registryLoading ? (
        <p className="text-sm text-slate-500">Discovering collections from runtime…</p>
      ) : !runtimeReady ? (
        <p className="text-sm text-slate-500">Connect a workspace to browse customer collections.</p>
      ) : registeredCollections.length === 0 ? (
        <div className="space-y-2 text-sm text-slate-500">
          <p>
            No collections found.
            {registryError
              ? " See the error above."
              : " Click Rediscover to scan the active runtime project."}
          </p>
          {!registryError && (
            <p className="text-[11px]">
              Tip: if rules block probing, register a known collection name with the field above.
            </p>
          )}
        </div>
      ) : (
        <>
          {validFavorites.length > 0 && (
            <Section title="Favorites">
              {validFavorites.map((c) => (
                <CollectionItem
                  key={`fav-${c}`}
                  entry={collectionMeta.get(c)}
                  active={collectionName === c}
                  starred
                  onSelect={() => selectCollection(c)}
                  onStar={() => toggleFavorite(c)}
                />
              ))}
            </Section>
          )}
          {validRecent.length > 0 && (
            <Section title="Recent">
              {validRecent.map((c) => (
                <CollectionItem
                  key={`rec-${c}`}
                  entry={collectionMeta.get(c)}
                  active={collectionName === c}
                  starred={favorites.includes(c)}
                  onSelect={() => selectCollection(c)}
                  onStar={() => toggleFavorite(c)}
                />
              ))}
            </Section>
          )}
          <Section title="Collections">
            {filtered.map((entry) => (
              <CollectionItem
                key={entry.collectionName}
                entry={entry}
                active={collectionName === entry.collectionName}
                starred={favorites.includes(entry.collectionName)}
                onSelect={() => selectCollection(entry.collectionName)}
                onStar={() => toggleFavorite(entry.collectionName)}
              />
            ))}
          </Section>
        </>
      )}
    </aside>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">{title}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function CollectionItem({ entry, active, starred, onSelect, onStar }) {
  if (!entry) return null;
  const { collectionName, documentCount, status } = entry;
  return (
    <div className={`flex items-center gap-1 rounded-lg px-2 py-1 ${active ? "bg-indigo-50 text-indigo-800" : ""}`}>
      <button type="button" className="min-w-0 flex-1 truncate text-left text-sm" onClick={onSelect}>
        <span>{collectionName}</span>
        {status === "empty" && (
          <span className="ml-1 rounded bg-slate-200 px-1 text-[9px] font-semibold uppercase text-slate-600">
            Empty
          </span>
        )}
        {typeof documentCount === "number" && (
          <span className="ml-1 text-[10px] text-slate-400">({documentCount})</span>
        )}
      </button>
      <button type="button" className="text-amber-500" onClick={onStar} title="Favorite">
        {starred ? "★" : "☆"}
      </button>
    </div>
  );
}
